/**
 * mining.ts — manual click + passive auto-mining onto the focused layer.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { safeNumber } from '../core/numbers';
import type { Derived } from './derived';
import { registerClick } from './combo';

/** Add ore to a stack, clamped to storage capacity. Returns the amount actually added. */
function addOre(state: GameState, oreId: string, amount: number, cap: number): number {
  const current = state.mining.ore[oreId] ?? 0;
  const next = Math.min(current + amount, cap);
  const added = Math.max(0, next - current);
  state.mining.ore[oreId] = next;
  state.stats.totalOreMined += added;
  state.stats.oreMined[oreId] = (state.stats.oreMined[oreId] ?? 0) + added;
  return added;
}

/** Passive mining for one tick. */
export function tickMining(state: GameState, dtMs: number, d: Derived): void {
  if (d.miningRate <= 0) return;
  const amount = safeNumber(d.miningRate * (dtMs / 1000));
  addOre(state, d.focusOreId, amount, d.storage);
}

/** A manual click on the seam. */
export function clickMine(state: GameState, d: Derived, bus?: EventBus): number {
  state.stats.totalClicks += 1;
  // d.clickPower already reflects the current combo; reward it, then build the combo.
  const added = addOre(state, d.focusOreId, d.clickPower, d.storage);
  registerClick(state);
  bus?.emit('mine:click', { oreId: d.focusOreId, amount: added });
  return added;
}
